<template>

  <div :data-address="localityName">

    <div class="application-additional-apartments-title">
      {{translate('Kohteen muut asunnot')}}
    </div>

    <LoadSpinner v-if="loading"/>

    <div
      v-if="!loading"
      class="application-additional-apartments__table">

        <table class="housing-locality-apartments__table">
          <template v-for="group in groups">
            <tbody :key="group.id" :data-id="group.id">

              <tr class="housing-locality-apartments__row housing-locality-apartments__row-first">
                  <td class="housing-locality-apartments__column-title">
                  {{translate(group.title)}}
                </td>
                <td class="housing-locality-apartment__cell-text">
                  {{translate('vapaana')}}
                </td>
                <td class="housing-locality-apartments__column-value">
                  {{group.areaString}}
                </td>
                <td class="housing-locality-apartments__column-value housing-locality-apartments__column-value--last">
                  {{sprintf(translate('%s kpl'), group.apartmentsAvailable.length)}}
                </td>
                <td class="housing-locality-apartments__column-favourite print-hide">
                  <button
                    v-if="group.apartmentsAvailable.length > 0"
                    :class="classFavouriteToggle(isFavouriteAll(group.apartmentsAvailable))"
                    @click.stop="onToggleGroup(group.apartmentsAvailable)"
                  >
                    <MinusIcon v-if="isFavouriteAll(group.apartmentsAvailable)"/>
                    <PlusIcon v-else/>

                    <span class="housing-locality-apartments__favourite-toggle-text">
                      {{getAddToApplicationBtnText(group.apartmentsAvailable)}}
                    </span>
                  </button>
                </td>

              </tr>
              <tr class="housing-locality-apartments__row">
                <td class="housing-locality-apartments__column-title">
                  &nbsp;
                </td>
                <td class="housing-locality-apartment__cell-text">
                  {{translate('varattuna')}}
                </td>
                <td class="housing-locality-apartments__column-value">
                  {{group.areaString}}
                </td>
                <td class="housing-locality-apartments__column-value housing-locality-apartments__column-value--last">
                  {{sprintf(translate('%s kpl'), group.apartmentsUnavailable.length)}}
                </td>
                <td class="housing-locality-apartments__column-favourite print-hide">
                  <button
                    v-if="group.apartmentsUnavailable.length > 0"
                    :class="classFavouriteToggle(isFavouriteAll(group.apartmentsUnavailable))"
                    @click.stop="onToggleGroup(group.apartmentsUnavailable)"
                  >
                    <MinusIcon v-if="isFavouriteAll(group.apartmentsUnavailable)"/>
                    <PlusIcon v-else/>

                    <span class="housing-locality-apartments__favourite-toggle-text">
                      {{getAddToApplicationBtnText(group.apartmentsUnavailable)}}
                    </span>
                  </button>
                </td>

              </tr>
            </tbody>
          </template>

        </table>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import {sprintf} from 'sprintf-js';

  import globals from '../../lib/globals';
  import {formatStringArea} from '../../lib/format';

  import utils from '../mixins/utils';
  import apartmentDisplayFields from '../mixins/apartmentDisplayFields';
  import LoadSpinner from './LoadSpinner';
  import MinusIcon from '../icons/MinusIcon.vue';
  import PlusIcon from '../icons/PlusIcon.vue';

  const groupTitles = {
    1: 'Yksiöt',
    2: 'Kaksiot',
    3: 'Kolmiot',
    4: 'Neliöt',
    5: '5+ huonetta',
  };

  export default {
    components: {
      LoadSpinner,
      MinusIcon,
      PlusIcon,
    },
    mixins: [
      apartmentDisplayFields,
      utils.translate,
      utils.apartmentUrl,
    ],
    props: {
      housingLocality: {
        type: String,
        required: true,
      },
      localityName: {
        type: String,
        required: true,
      }
    },
    data() {
      return {
        loading: false,
        groups: [],
      };
    },
    methods: {
      formatStringArea,
      sprintf,
      onToggleGroup(apartments) {
        this.toggleFavouriteAll(apartments);
      },
      /**
       * Check the apartment state.
       * VS Code couldn't follow, this is defined in index.js.
       * isFavourite: (state, getters) etc.
       *
       * @param apartment
       */
      isFavourite(apartment) {
        return this.$store.getters.isFavourite(apartment.mpc_asuntoid);
      },
      isFavouriteAll(apartments) {
        return apartments.every(this.isFavourite);
      },
      toggleFavourite(apartment) {
        const uuid = apartment.mpc_asuntoid;
        const {type} = apartment;
        const action = this.isFavourite(apartment) ? 'removeFavourite' : 'addFavourite';
        this.$store.commit(action, {uuid, type});
      },
      toggleFavouriteAll(apartments) {
        const action = this.isFavouriteAll(apartments) ?
          'removeFavourite' :
          'addFavourite';

        apartments.forEach(apartment => {
          const uuid = apartment.mpc_asuntoid;
          const {type} = apartment;
          this.$store.commit(action, {uuid, type});
        });
      },
      classFavouriteToggle(active) {
        return [
          'housing-locality-apartments__favourite-toggle',
          {
            'housing-locality-apartments__favourite-toggle--active': active
          },
        ];
      },
      /**
       * Note: We are handling two types of aparments here:
       * available and unavailable.
       *
       * @param apartments
       */
      getAddToApplicationBtnText(apartments) {

        return this.isFavouriteAll(apartments) ?
          this.translate('Kaikki lisätty hakemukselle') :
          this.translate( 'Lisää kaikki hakemukseen' );
      }
    },
    async mounted() {

      this.loading = true;
      const url = `${globals.urlIntegrationApi}/public/apartments-by-housing-locality/${this.housingLocality}`;
      const response = await axios.get(url);
      const apartments = response.data;

      const apartmentsGrouped = {};
      apartments.forEach(apartment => {
        const id = Math.min(apartment.room_count, 5);
        if (!apartmentsGrouped.hasOwnProperty(id)) {
          apartmentsGrouped[id] = [];
        }
        apartmentsGrouped[id].push(apartment);
      });

      const grouped = Object
        .entries(apartmentsGrouped)
        .sort((a, b) => a[0] === b[0] ? 0 : (a[0] < b[0] ? -1 : 1))
        .map(([id, apartments]) => {
          const areas = apartments.map(apartment => parseFloat(apartment.mpc_pinta_ala));
          const areaMin = areas.reduce((accumulator, area) => Math.min(accumulator, area));
          const areaMax = areas.reduce((accumulator, area) => Math.max(accumulator, area));
          const areaString = areaMin === areaMax
            ? formatStringArea(areaMin)
            : `${formatStringArea(areaMin, false)} - ${formatStringArea(areaMax)}`;
          const apartmentsAvailable = apartments.filter(apartment => apartment.available === true);
          const apartmentsUnavailable = apartments.filter(apartment => apartment.available === false);

          return {
            id,
            apartments,
            apartmentsAvailable,
            apartmentsUnavailable,
            title: groupTitles[id],
            areaString,
            open: false,
          };
        });

      this.loading = false;
      this.groups = grouped;
    },
  };
</script>

<style lang="scss">
  @import "~styles/definitions";

  .application-additional-apartments-title {
    font-size: 14px;
    line-height: 22px;
    font-weight: bold;
    margin: 22px 0 14px;
  }

  .application-additional-apartments__table {
    @include card;
    padding: 0.5rem 0;

      @include media-breakpoint-up(sm) {
        padding: 1rem 0;
      }

    .housing-locality-apartments__table {
      border-collapse: collapse;
    }

    .housing-locality-apartments__row {
      height: 2.75rem;
    }

    .housing-locality-apartment__cell-text {
      @include font-label-12;
      padding-right: r(8);
      //text-align: right;

      @include media-breakpoint-up(sm) {
        padding-right: r(12);
      }
    }

    .housing-locality-apartments__column-title {
      //@include housing-locality-apartments__column;
      //@include font-label-11;
      //padding: 0 r(10);

      text-transform: uppercase !important;
      font-weight: 900 !important;
      font-family: $ff-lato !important;
      width: auto;
    }

    tbody {
      border-bottom: 16px solid transparent;
    }
    tbody:last-child {
      border-bottom-width: 0;
    }

    .spacing-cell {
      width: 0%;

      @include media-breakpoint-up(sm) {
        width: 100%;
      }
    }
  }
</style>
